import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import '../styles/Admin.css';

interface NewsPost {
  _id?: string;
  title: string;
  content: string;
  excerpt?: string;
  author?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

function AdminNews() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPreview, setUploadingPreview] = useState(false);
  const [newsList, setNewsList] = useState<NewsPost[]>([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    verifyAuth();
    fetchNews();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Auth verification error:', error);
      localStorage.removeItem('adminToken');
      navigate('/admin/login');
    }
  };

  const fetchNews = async () => {
    try {
      const response = await fetch('/api/news');
      if (response.ok) {
        const data = await response.json();
        setNewsList(data.news || []);
      }
    } catch (error) {
      console.error('Fetch news error:', error);
    } finally {
      setLoadingNews(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this news post?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/news/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setMessage('News post deleted successfully!');
        fetchNews();
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage('Failed to delete news post');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/upload-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      const imageMarkdown = `![${file.name}](${data.url})`;
      setContent((prev) => prev + '\n' + imageMarkdown);
      setMessage('Image uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Image upload error:', error);
      setMessage('Image upload failed');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePreviewImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPreview(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/upload-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setPreviewImage(data.url);
      setMessage('Preview image uploaded!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Preview upload error:', error);
      setMessage('Preview upload failed');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setUploadingPreview(false);
    }
  };

  const handleNewsClick = (news: NewsPost) => {
    setEditingId(news._id!);
    setTitle(news.title);
    setContent(news.content);
    setExcerpt(news.excerpt || '');
    setPreviewImage((news as any).previewImage || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setTitle('');
    setContent('');
    setExcerpt('');
    setPreviewImage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingId ? `/api/admin/news/${editingId}` : '/api/admin/news';
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title,
          content,
          excerpt: excerpt || content.substring(0, 200),
          previewImage
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to ${editingId ? 'update' : 'create'} news post`);
      }

      setMessage(`News post ${editingId ? 'updated' : 'created'} successfully!`);
      setTitle('');
      setContent('');
      setExcerpt('');
      setPreviewImage('');
      setEditingId(null);
      fetchNews();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Submit error:', error);
      setMessage(`Failed to ${editingId ? 'update' : 'create'} news post`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="admin-container" data-color-mode="dark">
      <div className="admin-header">
        <div className="header-left">
          <h1>News Management</h1>
          {message && (
            <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
        </div>
        <div className="admin-actions">
          <button onClick={handleBackToHome} className="btn-secondary">
            Home
          </button>
          <button onClick={handleLogout} className="btn-secondary">
            Logout
          </button>
        </div>
      </div>

      <div className="admin-layout">
        {/* Left Sidebar - News List */}
        <div className="news-list-sidebar">
          <div className="sidebar-header">
            <h2>Published Posts</h2>
            <span className="count-badge">{newsList.length}</span>
          </div>
          {loadingNews ? (
            <div className="loading-text">Loading...</div>
          ) : newsList.length === 0 ? (
            <div className="empty-state">No posts yet</div>
          ) : (
            <div className="news-list">
              {newsList.map((news) => (
                <div
                  key={news._id}
                  className={`news-item ${editingId === news._id ? 'active' : ''}`}
                >
                  <div
                    className="news-item-content"
                    onClick={() => handleNewsClick(news)}
                  >
                    <h3>{news.title}</h3>
                    <p>{news.excerpt || news.content.substring(0, 80)}...</p>
                    <span className="news-meta">
                      {new Date(news.createdAt!).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(news._id!);
                    }}
                    className="btn-delete"
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Content - Editor */}
        <div className="editor-section">
          <form onSubmit={handleSubmit} className="news-form">
            {editingId && (
              <div className="editing-banner">
                <span>Editing post</span>
                <button type="button" onClick={handleCancelEdit} className="btn-cancel">
                  Cancel & Create New
                </button>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder="Enter news title..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="excerpt">Excerpt</label>
                <input
                  type="text"
                  id="excerpt"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Brief summary..."
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="preview-image">Preview Image (Optional)</label>
              <div className="preview-upload-section">
                <label htmlFor="preview-image-upload" className="btn-upload-inline">
                  {uploadingPreview ? 'Uploading...' : '+ Upload Preview'}
                </label>
                <input
                  type="file"
                  id="preview-image-upload"
                  accept="image/*"
                  onChange={handlePreviewImageUpload}
                  style={{ display: 'none' }}
                  disabled={uploadingPreview}
                />
                {previewImage && (
                  <div className="preview-image-container">
                    <img src={previewImage} alt="Preview" />
                    <button
                      type="button"
                      onClick={() => setPreviewImage('')}
                      className="btn-remove-preview"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="editor-row">
              <div className="editor-column">
                <div className="label-with-action">
                  <label>Content (Markdown)</label>
                  <label htmlFor="image-upload" className="btn-upload-inline">
                    {uploadingImage ? 'Uploading...' : '+ Image'}
                  </label>
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    disabled={uploadingImage}
                  />
                </div>
                <MDEditor
                  value={content}
                  onChange={(val) => setContent(val || '')}
                  height={500}
                  preview="edit"
                  hideToolbar={false}
                />
              </div>

              <div className="preview-column">
                <label>Live Preview</label>
                <div className="preview-content">
                  <MDEditor.Markdown source={content || '*Start typing to see preview...*'} />
                </div>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? (editingId ? 'Updating...' : 'Publishing...') : (editingId ? 'Update Post' : 'Publish Post')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminNews;
